import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin, from, EMPTY } from 'rxjs';
import { catchError, concatMap, map, mergeMap, toArray } from 'rxjs/operators';
import { Form, FormCreate, FormUpdate } from '@core/entities/models/form.model';
import { Beneficiary } from '@core/entities/models/beneficiary.model';
import { PaginatedList } from '@core/entities/models/paginated-list.model';
import { ApiEndpoints, API_BASE_URL } from '@core/config/api.config';
import { skipGlobalLoading } from '@core/interceptors/loading-context';
import { BeneficiaryService } from '@core/services/use-cases/beneficiary.service';

/**
 * Respuesta SUBMITTED de un beneficiario para un form, junto con el beneficiario.
 * Usado para exportar respuestas conservando la identidad del beneficiario (JRS-036).
 */
export interface BeneficiaryResponseEntry {
  beneficiary: Beneficiary;
  answers: Record<string, unknown>;
}

/**
 * Parámetros para listar formularios.
 * Según OpenAPI: filter, sortBy, sortOrder, offset, limit
 */
export interface FormListParams {
  offset?: number;
  limit?: number;
  /** Campo de ordenación según OpenAPI */
  sortBy?:
    | 'name'
    | 'createdAt'
    | 'updatedAt'
    | 'publication.assignments'
    | 'publication.submissions.total'
    | 'counts.partners'
    | 'email'
    | 'firstName'
    | 'lastName';
  sortOrder?: 'ASC' | 'DESC';
  /** Filtro de búsqueda (max 128 chars) */
  filter?: string;
}

/**
 * Respuesta del listado de formularios.
 */
export interface FormListResponse {
  limit: number;
  offset: number;
  total: number;
  items: Form[];
}

/**
 * Servicio para gestionar formularios.
 * Trabaja directamente con la API de Forms según OpenAPI spec.
 */
/** Concurrencia máxima al agregar respuestas vía N+1 (ver §3.3 del plan JRS-037). */
const DASHBOARD_RESPONSE_CONCURRENCY = 10;

/** Page size al listar beneficiarios para agregación de respuestas. */
const DASHBOARD_BENEFICIARY_PAGE_SIZE = 100;

@Injectable({ providedIn: 'root' })
export class FormService {
  private readonly http = inject(HttpClient);
  private readonly beneficiaryService = inject(BeneficiaryService);

  // ---------------------------------------------------------------------------
  // API pública - Operaciones CRUD
  // ---------------------------------------------------------------------------

  /**
   * Obtiene todos los formularios.
   * GET /forms
   *
   * @param params - Parámetros de paginación y filtrado
   * @returns Observable con el listado de formularios
   */
  getAll(params?: FormListParams): Observable<PaginatedList<Form>> {
    let httpParams = {};

    if (params?.offset !== undefined) {
      httpParams = { ...httpParams, offset: params.offset };
    }
    if (params?.limit !== undefined) {
      httpParams = { ...httpParams, limit: params.limit };
    }
    if (params?.filter) {
      httpParams = { ...httpParams, filter: params.filter };
    }
    if (params?.sortBy) {
      httpParams = { ...httpParams, sortBy: params.sortBy };
    }
    if (params?.sortOrder) {
      httpParams = { ...httpParams, sortOrder: params.sortOrder };
    }

    return this.http.get<FormListResponse>(ApiEndpoints.forms.base, {
      params: httpParams,
      context: skipGlobalLoading(),
    });
  }

  /**
   * Obtiene un formulario por su ID.
   * GET /forms/{id}
   *
   * @param id - Identificador único del formulario
   * @returns Observable con el formulario solicitado
   */
  getById(id: string): Observable<Form> {
    return this.http.get<Form>(ApiEndpoints.forms.byId(id));
  }

  /**
   * Crea un nuevo formulario.
   * POST /forms
   *
   * @param form - Datos del formulario a crear
   * @returns Observable con el formulario creado
   */
  create(form: FormCreate): Observable<Form> {
    return this.http.post<Form>(ApiEndpoints.forms.base, form);
  }

  /**
   * Actualiza un formulario existente.
   * PUT /forms/{id}
   *
   * @param id - Identificador del formulario a actualizar
   * @param form - Datos actualizados del formulario
   * @returns Observable con el formulario actualizado
   */
  update(id: string, form: FormUpdate): Observable<Form> {
    return this.http.put<Form>(ApiEndpoints.forms.byId(id), form);
  }

  /**
   * Elimina un formulario.
   * DELETE /forms/{id}
   *
   * @param id - Identificador del formulario a eliminar
   * @returns Observable vacío cuando el formulario se elimina correctamente
   */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(ApiEndpoints.forms.byId(id));
  }

  /**
   * Finaliza un formulario (cambia status de DRAFT a FINAL).
   * POST /forms/{formId}/finalize
   * Una vez finalizado, el formulario no puede ser modificado.
   *
   * @param id - Identificador del formulario a finalizar
   * @returns Observable vacío cuando el formulario se finaliza correctamente
   */
  finalize(id: string): Observable<void> {
    return this.http.post<void>(ApiEndpoints.forms.finalize(id), {});
  }

  // ---------------------------------------------------------------------------
  // API contextual - Operaciones según contexto (country/partner)
  // ---------------------------------------------------------------------------

  /**
   * Obtiene todos los formularios de un country.
   * GET /countries/{countryId}/forms
   *
   * @param countryId - Identificador del country
   * @param params - Parámetros de paginación y filtrado
   * @returns Observable con el listado de formularios del country
   */
  getAllInCountry(countryId: string, params?: FormListParams): Observable<FormListResponse> {
    let httpParams = {};

    if (params?.offset !== undefined) {
      httpParams = { ...httpParams, offset: params.offset };
    }
    if (params?.limit !== undefined) {
      httpParams = { ...httpParams, limit: params.limit };
    }
    if (params?.filter) {
      httpParams = { ...httpParams, filter: params.filter };
    }
    if (params?.sortBy) {
      httpParams = { ...httpParams, sortBy: params.sortBy };
    }
    if (params?.sortOrder) {
      httpParams = { ...httpParams, sortOrder: params.sortOrder };
    }

    return this.http.get<FormListResponse>(`${API_BASE_URL}/countries/${countryId}/forms`, {
      params: httpParams,
      context: skipGlobalLoading(),
    });
  }

  /**
   * Obtiene un formulario por su ID en el contexto de un country.
   * GET /countries/{countryId}/forms/{formId}
   *
   * @param countryId - Identificador del country
   * @param formId - Identificador del formulario
   * @returns Observable con el formulario solicitado
   */
  getByIdInCountry(countryId: string, formId: string): Observable<Form> {
    return this.http.get<Form>(ApiEndpoints.forms.byCountryId(countryId, formId));
  }

  /**
   * Obtiene todos los formularios de un partner.
   * GET /partners/{partnerId}/forms
   *
   * @param partnerId - Identificador del partner
   * @param params - Parámetros de paginación y filtrado
   * @returns Observable con el listado de formularios del partner
   */
  getAllInPartner(partnerId: string, params?: FormListParams): Observable<FormListResponse> {
    let httpParams = {};

    if (params?.offset !== undefined) {
      httpParams = { ...httpParams, offset: params.offset };
    }
    if (params?.limit !== undefined) {
      httpParams = { ...httpParams, limit: params.limit };
    }
    if (params?.filter) {
      httpParams = { ...httpParams, filter: params.filter };
    }
    if (params?.sortBy) {
      httpParams = { ...httpParams, sortBy: params.sortBy };
    }
    if (params?.sortOrder) {
      httpParams = { ...httpParams, sortOrder: params.sortOrder };
    }

    return this.http.get<FormListResponse>(`${API_BASE_URL}/partners/${partnerId}/forms`, {
      params: httpParams,
      context: skipGlobalLoading(),
    });
  }

  /**
   * Obtiene un formulario por su ID en el contexto de un partner.
   * GET /partners/{partnerId}/forms/{formId}
   *
   * @param partnerId - Identificador del partner
   * @param formId - Identificador del formulario
   * @returns Observable con el formulario solicitado
   */
  getByIdInPartner(partnerId: string, formId: string): Observable<Form> {
    return this.http.get<Form>(ApiEndpoints.forms.byPartnerId(partnerId, formId));
  }

  /**
   * Publica un formulario en un country.
   * PUT /countries/{countryId}/forms/{formId}/publication
   *
   * @param countryId - Identificador del country
   * @param formId - Identificador del formulario
   * @returns Observable con el formulario publicado
   */
  publishToCountry(countryId: string, formId: string): Observable<Form> {
    return this.http.put<Form>(
      `${API_BASE_URL}/countries/${countryId}/forms/${formId}/publication`,
      {},
    );
  }

  /**
   * Publica un formulario en un partner.
   * PUT /partners/{partnerId}/forms/{formId}/publication
   *
   * @param partnerId - Identificador del partner
   * @param formId - Identificador del formulario
   * @param target - Número objetivo para el formulario
   * @returns Observable con el formulario publicado
   */
  publishToPartner(partnerId: string, formId: string, target: number): Observable<Form> {
    return this.http.put<Form>(
      `${API_BASE_URL}/partners/${partnerId}/forms/${formId}/publication`,
      { target },
    );
  }

  // ---------------------------------------------------------------------------
  // API de Beneficiarios - Formularios y respuestas
  // ---------------------------------------------------------------------------

  /**
   * Obtiene todos los formularios disponibles para un beneficiario.
   * GET /beneficiaries/{beneficiaryId}/forms
   *
   * @param beneficiaryId - Identificador del beneficiario
   * @param params - Parámetros de paginación y filtrado
   * @returns Observable con el listado de formularios del beneficiario
   */
  getAllInBeneficiary(
    beneficiaryId: string,
    params?: FormListParams,
  ): Observable<FormListResponse> {
    let httpParams = {};

    if (params?.offset !== undefined) {
      httpParams = { ...httpParams, offset: params.offset };
    }
    if (params?.limit !== undefined) {
      httpParams = { ...httpParams, limit: params.limit };
    }
    if (params?.filter) {
      httpParams = { ...httpParams, filter: params.filter };
    }
    if (params?.sortBy) {
      httpParams = { ...httpParams, sortBy: params.sortBy };
    }
    if (params?.sortOrder) {
      httpParams = { ...httpParams, sortOrder: params.sortOrder };
    }

    return this.http.get<FormListResponse>(ApiEndpoints.beneficiaries.forms(beneficiaryId), {
      params: httpParams,
      context: skipGlobalLoading(),
    });
  }

  /**
   * Obtiene un formulario por su ID en el contexto de un beneficiario.
   * GET /beneficiaries/{beneficiaryId}/forms/{formId}
   *
   * @param beneficiaryId - Identificador del beneficiario
   * @param formId - Identificador del formulario
   * @returns Observable con el formulario solicitado
   */
  getByIdInBeneficiary(beneficiaryId: string, formId: string): Observable<Form> {
    return this.http.get<Form>(ApiEndpoints.beneficiaries.formById(beneficiaryId, formId));
  }

  /**
   * Guarda o actualiza el borrador de respuesta de un formulario.
   * PUT /beneficiaries/{beneficiaryId}/forms/{formId}/response
   *
   * @param beneficiaryId - Identificador del beneficiario
   * @param formId - Identificador del formulario
   * @param answers - Respuestas del formulario
   * @returns Observable con la respuesta guardada
   */
  saveFormResponse(
    beneficiaryId: string,
    formId: string,
    answers: Record<string, unknown>,
  ): Observable<FormResponse> {
    return this.http.put<FormResponse>(
      ApiEndpoints.beneficiaries.formResponse(beneficiaryId, formId),
      { answers },
    );
  }

  /**
   * Envía/finaliza la respuesta de un formulario (bloquea futuras ediciones).
   * POST /beneficiaries/{beneficiaryId}/forms/{formId}/response/submit
   *
   * @param beneficiaryId - Identificador del beneficiario
   * @param formId - Identificador del formulario
   * @returns Observable con la respuesta finalizada
   */
  submitFormResponse(beneficiaryId: string, formId: string): Observable<FormResponse> {
    return this.http.post<FormResponse>(
      ApiEndpoints.beneficiaries.formResponseSubmit(beneficiaryId, formId),
      {},
    );
  }

  /**
   * Obtiene todos los formularios para exportación (hace múltiples llamadas hasta obtener todos)
   */
  getAllForExport(params?: FormListParams): Observable<Form[]> {
    return this.getAll({ ...params, offset: 0, limit: 100 }).pipe(
      concatMap((firstResponse) => {
        const total = firstResponse.total;

        if (total <= 100 || total === 0) {
          return of(firstResponse.items);
        }

        const totalPages = Math.ceil(total / 100);
        const requests: Observable<PaginatedList<Form>>[] = [of(firstResponse)];

        for (let page = 1; page < totalPages; page++) {
          const offset = page * 100;
          requests.push(this.getAll({ ...params, offset, limit: 100 }));
        }

        return forkJoin(requests).pipe(
          map((responses) => {
            const allForms: Form[] = [];
            responses.forEach((response) => {
              allForms.push(...response.items);
            });
            return allForms;
          }),
        );
      }),
    );
  }

  /**
   * Obtiene todos los formularios de un country para exportación (hace múltiples llamadas hasta obtener todos)
   */
  getAllForExportInCountry(countryId: string, params?: FormListParams): Observable<Form[]> {
    return this.getAllInCountry(countryId, { ...params, offset: 0, limit: 100 }).pipe(
      concatMap((firstResponse) => {
        const total = firstResponse.total;

        if (total <= 100 || total === 0) {
          return of(firstResponse.items);
        }

        const totalPages = Math.ceil(total / 100);
        const requests: Observable<FormListResponse>[] = [of(firstResponse)];

        for (let page = 1; page < totalPages; page++) {
          const offset = page * 100;
          requests.push(this.getAllInCountry(countryId, { ...params, offset, limit: 100 }));
        }

        return forkJoin(requests).pipe(
          map((responses) => {
            const allForms: Form[] = [];
            responses.forEach((response) => {
              allForms.push(...response.items);
            });
            return allForms;
          }),
        );
      }),
    );
  }

  /**
   * Obtiene todos los formularios de un partner para exportación (hace múltiples llamadas hasta obtener todos)
   */
  getAllForExportInPartner(partnerId: string, params?: FormListParams): Observable<Form[]> {
    return this.getAllInPartner(partnerId, { ...params, offset: 0, limit: 100 }).pipe(
      concatMap((firstResponse) => {
        const total = firstResponse.total;

        if (total <= 100 || total === 0) {
          return of(firstResponse.items);
        }

        const totalPages = Math.ceil(total / 100);
        const requests: Observable<FormListResponse>[] = [of(firstResponse)];

        for (let page = 1; page < totalPages; page++) {
          const offset = page * 100;
          requests.push(this.getAllInPartner(partnerId, { ...params, offset, limit: 100 }));
        }

        return forkJoin(requests).pipe(
          map((responses) => {
            const allForms: Form[] = [];
            responses.forEach((response) => {
              allForms.push(...response.items);
            });
            return allForms;
          }),
        );
      }),
    );
  }

  /**
   * Obtiene todos los formularios de un beneficiario para exportación (hace múltiples llamadas hasta obtener todos)
   */
  getAllForExportInBeneficiary(beneficiaryId: string, params?: FormListParams): Observable<Form[]> {
    return this.getAllInBeneficiary(beneficiaryId, { ...params, offset: 0, limit: 100 }).pipe(
      concatMap((firstResponse) => {
        const total = firstResponse.total;

        if (total <= 100 || total === 0) {
          return of(firstResponse.items);
        }

        const totalPages = Math.ceil(total / 100);
        const requests: Observable<FormListResponse>[] = [of(firstResponse)];

        for (let page = 1; page < totalPages; page++) {
          const offset = page * 100;
          requests.push(this.getAllInBeneficiary(beneficiaryId, { ...params, offset, limit: 100 }));
        }

        return forkJoin(requests).pipe(
          map((responses) => {
            const allForms: Form[] = [];
            responses.forEach((response) => {
              allForms.push(...response.items);
            });
            return allForms;
          }),
        );
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // API pública - Formularios públicos (sin autenticación)
  // ---------------------------------------------------------------------------

  /**
   * Endpoint único para gestionar formularios públicos mediante token.
   * POST /form-responses/{token}
   *
   * Este endpoint NO requiere autenticación (security: [])
   * Se usa para todos los pasos del flujo público:
   *
   * Paso 1: Verificar beneficiario → {email, birthDate}
   *   - 200: beneficiario existe, devuelve formulario + respuestas
   *   - 404: beneficiario no existe
   *
   * Paso 2b: Crear beneficiario → {email, birthDate, beneficiary: {...}}
   *
   * Paso 3: Guardar borrador → {email, birthDate, response: {answers: {...}, submit: false}}
   *
   * Paso 4: Finalizar formulario → {email, birthDate, response: {answers: {...}, submit: true}}
   *
   * @param token - Token UUID del formulario publicado
   * @param body - Body del request según el paso
   * @returns Observable con la respuesta del formulario
   */
  postFormResponse(token: string, body: FormResponseRequest): Observable<FormResponseResult> {
    const url = `${API_BASE_URL}/form-responses/${token}`;
    return this.http.post<FormResponseResult>(url, body);
  }

  // ---------------------------------------------------------------------------
  // Agregación de respuestas para Dashboards (JRS-037)
  // ---------------------------------------------------------------------------
  //
  // El OpenAPI actual no expone un endpoint que devuelva todas las respuestas
  // de un form a nivel global/country/partner. Estos helpers agregan en cliente
  // mediante N+1 (listado de beneficiarios + un GET por cada uno) con
  // concurrencia controlada y sin disparar el spinner global.
  // ---------------------------------------------------------------------------

  /**
   * Devuelve las answers SUBMITTED de un beneficiario para un form, o null
   * si no existe respuesta o aún no se ha enviado.
   */
  getSubmittedAnswersForBeneficiary(
    beneficiaryId: string,
    formId: string,
  ): Observable<Record<string, unknown> | null> {
    return this.http
      .get<Form & { response?: { status?: string; answers?: Record<string, unknown> } }>(
        ApiEndpoints.beneficiaries.formById(beneficiaryId, formId),
        { context: skipGlobalLoading() },
      )
      .pipe(
        map((form) =>
          form.response?.status === 'SUBMITTED' ? form.response.answers ?? {} : null,
        ),
        catchError(() => of(null)),
      );
  }

  /** Recolecta todas las respuestas SUBMITTED de un form globalmente. */
  getAllSubmittedAnswersForForm(formId: string): Observable<Array<Record<string, unknown>>> {
    return this.aggregateAnswersAcrossBeneficiaries(formId, {});
  }

  /** Recolecta todas las respuestas SUBMITTED de un form en un country. */
  getAllSubmittedAnswersForFormInCountry(
    countryId: string,
    formId: string,
  ): Observable<Array<Record<string, unknown>>> {
    return this.aggregateAnswersAcrossBeneficiaries(formId, { countryId });
  }

  /** Recolecta todas las respuestas SUBMITTED de un form en un partner. */
  getAllSubmittedAnswersForFormInPartner(
    partnerId: string,
    formId: string,
  ): Observable<Array<Record<string, unknown>>> {
    return this.aggregateAnswersAcrossBeneficiaries(formId, { partnerId });
  }

  /**
   * Recolecta todas las respuestas SUBMITTED de un form en un partner, conservando
   * el beneficiario asociado a cada respuesta (JRS-036, exportación a Excel).
   */
  getAllSubmittedResponsesWithBeneficiaryInPartner(
    partnerId: string,
    formId: string,
  ): Observable<BeneficiaryResponseEntry[]> {
    return this.aggregateResponsesAcrossBeneficiaries(formId, { partnerId });
  }

  /**
   * Lista todos los beneficiarios que cumplan el scope y, para cada uno,
   * consulta su respuesta SUBMITTED para el form. Devuelve el array filtrado
   * de answers (ignora los null / errores individuales).
   */
  private aggregateAnswersAcrossBeneficiaries(
    formId: string,
    scope: { countryId?: string; partnerId?: string },
  ): Observable<Array<Record<string, unknown>>> {
    return this.aggregateResponsesAcrossBeneficiaries(formId, scope).pipe(
      map((entries) => entries.map((e) => e.answers)),
    );
  }

  /**
   * Como `aggregateAnswersAcrossBeneficiaries`, pero conserva el beneficiario
   * asociado a cada respuesta SUBMITTED (ignora los que no tienen respuesta enviada).
   */
  private aggregateResponsesAcrossBeneficiaries(
    formId: string,
    scope: { countryId?: string; partnerId?: string },
  ): Observable<BeneficiaryResponseEntry[]> {
    return this.listAllBeneficiariesInScope(scope).pipe(
      concatMap((beneficiaries) => {
        if (beneficiaries.length === 0) return of([] as BeneficiaryResponseEntry[]);

        return from(beneficiaries).pipe(
          mergeMap(
            (b) =>
              this.getSubmittedAnswersForBeneficiary(b.id, formId).pipe(
                map((answers): BeneficiaryResponseEntry | null =>
                  answers ? { beneficiary: b, answers } : null,
                ),
              ),
            DASHBOARD_RESPONSE_CONCURRENCY,
          ),
          toArray(),
          map((entries) =>
            entries.filter((e): e is BeneficiaryResponseEntry => e !== null && e !== undefined),
          ),
        );
      }),
    );
  }

  /**
   * Recupera todos los beneficiarios de un scope (country/partner) recorriendo
   * todas las páginas. Sin spinner global (`skipGlobalLoading` ya aplicado en el servicio).
   */
  private listAllBeneficiariesInScope(scope: {
    countryId?: string;
    partnerId?: string;
  }): Observable<Beneficiary[]> {
    return this.beneficiaryService
      .getAll({ ...scope, offset: 0, limit: DASHBOARD_BENEFICIARY_PAGE_SIZE })
      .pipe(
        concatMap((firstPage) => {
          const total = firstPage.total;

          if (total <= DASHBOARD_BENEFICIARY_PAGE_SIZE) {
            return of(firstPage.items);
          }

          const totalPages = Math.ceil(total / DASHBOARD_BENEFICIARY_PAGE_SIZE);
          const remainingPages: Array<Observable<PaginatedList<Beneficiary>>> = [];
          for (let page = 1; page < totalPages; page++) {
            remainingPages.push(
              this.beneficiaryService.getAll({
                ...scope,
                offset: page * DASHBOARD_BENEFICIARY_PAGE_SIZE,
                limit: DASHBOARD_BENEFICIARY_PAGE_SIZE,
              }),
            );
          }

          return forkJoin(remainingPages).pipe(
            map((restPages) =>
              restPages.reduce((acc, p) => acc.concat(p.items), firstPage.items as Beneficiary[]),
            ),
          );
        }),
      );
  }
}

/**
 * Body del request para POST /form-responses/{token}
 */
export interface FormResponseRequest {
  email: string;
  /** Segundo factor de identidad del beneficiario en el flujo público. Formato ISO `YYYY-MM-DD`. */
  birthDate: string;
  beneficiary?: {
    firstName: string;
    lastName: string;
    /** El teléfono va DENTRO de beneficiary; `birthDate` va a nivel raíz (identidad). Ver CreateFormResponse en el OpenAPI. */
    phone: string;
    gender: string;
    birthCountry: string;
    hostCountry: string;
    /** Requerido por la API (puede ir vacío, minLength 0). Ver CreateFormResponse en el OpenAPI. */
    externalId: string;
  };
  response?: {
    answers: Record<string, unknown>;
    submit: boolean;
  };
}

/**
 * Respuesta del endpoint POST /form-responses/{token}
 */
export interface FormResponseResult {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  schema: Record<string, unknown>;
  response?: {
    id: string;
    createdAt: string;
    updatedAt: string;
    status: 'DRAFT' | 'SUBMITTED';
    answers: Record<string, unknown>;
  };
}

/**
 * @deprecated Usar FormResponseResult en su lugar
 */
export interface FormResponse {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  schema: Record<string, unknown>;
  response?: {
    id: string;
    createdAt: string;
    updatedAt: string;
    status: 'DRAFT' | 'SUBMITTED';
    answers: Record<string, unknown>;
  };
}
